import { passes, type ColumnSelection } from './columnFilters'
import { COVERAGE_REPORT_STATUS_LABEL, WHOLE_SHIFT_LABEL, worstStatus, type CoverageReportRow, type CoverageReportStatus } from './coverageReport'
import type { ExportColumn, ExportSummary } from './reportExport'

/**
 * Staffing against the staffing targets, for the reports that list people: how
 * many the targets require, how many are available, and by how much each
 * assignment falls short or runs over. Every assignment has its own target, so
 * every assignment has its own line; a total sits beneath them. Built from the
 * same coverage rows the daily coverage report reads, so the figures agree with
 * it. Pure, so the screen and the exports read one result.
 */
export type StaffingRow = {
  key: string
  // The row's identifying columns, such as unit, shift, and assignment.
  labels: string[]
  required: number
  available: number
  // Headcount short of a target, and above one. Each assignment counts on its
  // own, so a surplus in one does not cover a shortage in another.
  short: number
  over: number
  status: CoverageReportStatus
}

export type StaffingSummary = { labelHeaders: string[]; rows: StaffingRow[]; total: StaffingRow }

const shortBy = (row: Pick<CoverageReportRow, 'required' | 'available'>) => (row.required === null ? 0 : Math.max(0, row.required - row.available))
const overBy = (row: Pick<CoverageReportRow, 'required' | 'available'>) => (row.required === null ? 0 : Math.max(0, row.available - row.required))

type Line = { sortKey: string; date: string; team: string; shift: string; assignment: string; row: StaffingRow }

const lineOf = (row: CoverageReportRow): StaffingRow => ({
  key: '',
  labels: [],
  required: row.required ?? 0,
  available: row.available,
  short: shortBy(row),
  over: overBy(row),
  status: row.status,
})

// The lines of one date, team, and shift. A target on the assignments is
// measured on each of them; when none has one, a target on the whole shift is
// measured on the shift-wide row, which restates the assignments beneath it, so
// it stands in for them. The status of the group as a whole is its most urgent.
function linesOf(rows: CoverageReportRow[]): { lines: Line[]; statuses: CoverageReportStatus[] } {
  const groups = new Map<string, CoverageReportRow[]>()
  for (const row of rows) {
    const key = `${row.date}|${row.teamId}|${row.shiftId ?? ''}`
    groups.set(key, [...(groups.get(key) ?? []), row])
  }
  const lines: Line[] = []
  const statuses: CoverageReportStatus[] = []
  for (const [key, group] of groups) {
    const assignments = group.filter((row) => row.assignment !== WHOLE_SHIFT_LABEL)
    const wholeShift = group.find((row) => row.assignment === WHOLE_SHIFT_LABEL)
    const wholeMeasured = !assignments.some((row) => row.required !== null) && !!wholeShift && wholeShift.required !== null
    const shown = wholeMeasured && wholeShift ? [wholeShift] : assignments
    for (const row of shown) {
      lines.push({ sortKey: `${key}|${row.team}|${row.shift}|${row.assignment}`, date: row.date, team: row.team, shift: row.shift, assignment: row.assignment, row: lineOf(row) })
    }
    statuses.push(...group.map((row) => row.status))
  }
  return { lines, statuses }
}

function totalOf(lines: Line[], statuses: CoverageReportStatus[]): StaffingRow {
  const sum = (pick: (row: StaffingRow) => number) => lines.reduce((total, line) => total + pick(line.row), 0)
  return {
    key: 'total',
    labels: ['Total'],
    required: sum((row) => row.required),
    available: sum((row) => row.available),
    short: sum((row) => row.short),
    over: sum((row) => row.over),
    status: worstStatus(statuses),
  }
}

function summarize(rows: CoverageReportRow[], labelHeaders: string[], labelsOf: (line: Line) => string[]): StaffingSummary {
  const { lines, statuses } = linesOf(rows)
  const ordered = [...lines].sort((a, b) => a.sortKey.localeCompare(b.sortKey))
  return {
    labelHeaders,
    rows: ordered.map((line) => ({ ...line.row, key: line.sortKey, labels: labelsOf(line) })),
    total: totalOf(lines, statuses),
  }
}

/** One row for each assignment of each unit and shift on the rows given. */
export const staffingByAssignment = (rows: CoverageReportRow[]) =>
  summarize(rows, ['Unit', 'Shift', 'Assignment'], (line) => [line.team, line.shift, line.assignment])

/** One row for each assignment on each day on the rows given, the day named by `labelFor`. */
export const staffingByDayAssignment = (rows: CoverageReportRow[], labelFor: (date: string) => string = (date) => date) =>
  summarize(rows, ['Day', 'Unit', 'Shift', 'Assignment'], (line) => [labelFor(line.date), line.team, line.shift, line.assignment])

/** The values each column of the staffing table is narrowed to, by heading; Status is by status. */
export type StaffingFilters = Record<string, ColumnSelection>

export const STAFFING_STATUS_HEADER = 'Status'

/**
 * The rows the filters leave, with a total of just those rows, so the total
 * always adds up what is shown. With no filter in force the summary is returned
 * as it came.
 */
export function filterStaffing(summary: StaffingSummary, filters: StaffingFilters): StaffingSummary {
  if (Object.values(filters).every((selected) => selected === undefined)) return summary
  const rows = summary.rows.filter(
    (row) => summary.labelHeaders.every((header, index) => passes(filters[header], row.labels[index])) && passes(filters[STAFFING_STATUS_HEADER], row.status),
  )
  const sum = (pick: (row: StaffingRow) => number) => rows.reduce((total, row) => total + pick(row), 0)
  return {
    ...summary,
    rows,
    total: { ...summary.total, required: sum((row) => row.required), available: sum((row) => row.available), short: sum((row) => row.short), over: sum((row) => row.over), status: worstStatus(rows.map((row) => row.status)) },
  }
}

export const STAFFING_VALUE_HEADERS = ['Required', 'Available', 'Short by', 'Over by', 'Status']

export const staffingStatusLabel = (status: CoverageReportStatus) => COVERAGE_REPORT_STATUS_LABEL[status]

/** The summary as a block of an export: the label columns, the figures, then a total row. */
export function staffingExportSummary(title: string, summary: StaffingSummary): ExportSummary {
  const columns: ExportColumn[] = [
    ...summary.labelHeaders.map((header) => ({ header, width: 18 })),
    { header: 'Required', numeric: true },
    { header: 'Available', numeric: true },
    { header: 'Short by', numeric: true },
    { header: 'Over by', numeric: true },
    { header: 'Status', width: 22 },
  ]
  const line = (row: StaffingRow) => [
    ...row.labels,
    ...Array.from({ length: summary.labelHeaders.length - row.labels.length }, () => ''),
    row.required,
    row.available,
    row.short,
    row.over,
    staffingStatusLabel(row.status),
  ]
  return { title, columns, rows: summary.rows.map(line), total: line(summary.total) }
}
